// =============================================================================
// components/ui/Header.tsx — the one in-screen header.
//
// WHY THIS EXISTS
// Every tab / custom-header screen re-implemented the same row:
//   { flexDirection:'row', paddingHorizontal: lg, paddingVertical: md }
// with hand-placed titles and action icons — so heights and title alignment
// drifted between Home / Markets / Chat / Profile. Header fixes one consistent
// height (layout.headerHeight) and one alignment, and standardises the
// back-button + actions slots.
//
// MODES
//   - Large title (tab screens):  <Header title="Markets" titleSize="large" right={…} />
//   - Back + centred title (detail/pushed):
//                                 <Header title="Wallet" onBack={() => navigation.goBack()} />
//
// It does NOT add a safe-area top inset itself — Screen owns that. Place a
// Header as the first child of a <Screen> (the Screen's top padding lifts it
// clear of the notch / Dynamic Island).
//
// `glass` opts the bar into the Glass Contract (GlassBar). Default is the flat
// transparent header (matches the current tab screens) so migrating a screen is
// a pure structural change with no visual regression.
//
// BARE-RN: @expo/vector-icons Ionicons → local Icon shim.
// =============================================================================
import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ViewStyle,
  StyleProp,
} from 'react-native';
import { Ionicons } from './Icon';
import { colors, colorTokens, spacing, fonts, layout as layoutTokens, ThemeMode } from '../../constants/theme';
import GlassBar from '../glass/GlassBar';

export interface HeaderProps {
  /** Title text. Optional — a header can be icons-only (e.g. a brand logo left). */
  title?: string;
  /** Custom node rendered in place of the title text (e.g. the CRYB wordmark). */
  titleNode?: React.ReactNode;
  /** 'large' = left-aligned bold (tab screens); 'default' = centred (detail). */
  titleSize?: 'large' | 'default';
  /** Show a back chevron on the left; fires this on press. */
  onBack?: () => void;
  /** Left slot (overrides the back button when provided). */
  left?: React.ReactNode;
  /** Right slot — action icons. */
  right?: React.ReactNode;
  /** Render the bar on a GlassBar surface instead of flat/transparent. */
  glass?: boolean;
  /** Theme mode. 'light' (default) or 'dark' (voice/call). Additive. */
  theme?: ThemeMode;
  style?: StyleProp<ViewStyle>;
}

export default function Header({
  title,
  titleNode,
  titleSize = 'large',
  onBack,
  left,
  right,
  glass = false,
  theme = 'light',
  style,
}: HeaderProps) {
  const centred = titleSize === 'default';
  const tc = colorTokens[theme];

  const leftSlot =
    left ??
    (onBack ? (
      <TouchableOpacity
        onPress={onBack}
        style={styles.iconBtn}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel="Go back"
      >
        <Ionicons name="chevron-back" size={26} color={tc.text} />
      </TouchableOpacity>
    ) : null);

  const titleEl =
    titleNode ??
    (title ? (
      <Text
        style={[centred ? styles.titleCentered : styles.titleLarge, { color: tc.text }]}
        numberOfLines={1}
      >
        {title}
      </Text>
    ) : null);

  // Centred mode needs symmetric left/right gutters so the title is truly
  // centred regardless of which slots are present.
  const inner = centred ? (
    <View style={styles.rowCentered}>
      <View style={styles.sideSlot}>{leftSlot}</View>
      <View style={styles.centerSlot} pointerEvents="box-none">
        {titleEl}
      </View>
      <View style={[styles.sideSlot, styles.sideRight]}>{right}</View>
    </View>
  ) : (
    <View style={styles.rowLarge}>
      {leftSlot}
      <View style={styles.largeTitleWrap}>{titleEl}</View>
      <View style={styles.rightSlot}>{right}</View>
    </View>
  );

  if (glass) {
    return (
      <GlassBar style={[styles.glassWrap, style]} contentStyle={styles.glassContent}>
        {inner}
      </GlassBar>
    );
  }

  return <View style={[styles.flat, style]}>{inner}</View>;
}

const styles = StyleSheet.create({
  flat: {
    paddingHorizontal: layoutTokens.gutter,
    justifyContent: 'center',
    minHeight: layoutTokens.headerHeight,
    paddingVertical: spacing.sm,
  },
  glassWrap: {
    marginHorizontal: layoutTokens.gutter,
    marginTop: spacing.sm,
  },
  glassContent: {
    paddingHorizontal: spacing.lg,
    minHeight: layoutTokens.headerHeight,
    justifyContent: 'center',
  },
  // ── Large (left-aligned) ──
  rowLarge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: layoutTokens.headerHeight,
  },
  largeTitleWrap: {
    flex: 1,
  },
  titleLarge: {
    ...fonts.bold,
    fontSize: 22,
    color: colors.ink,
  },
  rightSlot: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  // ── Centred (back + title) ──
  rowCentered: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: layoutTokens.headerHeight,
  },
  sideSlot: {
    minWidth: 44,
    flexDirection: 'row',
    alignItems: 'center',
  },
  sideRight: {
    justifyContent: 'flex-end',
    gap: spacing.md,
  },
  centerSlot: {
    flex: 1,
    alignItems: 'center',
  },
  titleCentered: {
    ...fonts.semibold,
    fontSize: 17,
    color: colors.ink,
  },
  iconBtn: {
    minWidth: 44,
    minHeight: 44,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
});
